/**
 * Back Button
 *
 * @module app/components/BackButton
 *
 * PubSub topics documentation:
 *
 * # Inserts a history go back event
 * Topic: BackButton.history
 * Value: {integer} Integer step value, i.e. simple history go back value is -1
 *
 * # Inserts a navigate in page event
 * Topic: BackButton.navigate
 * Value: {String} A string value to the path to navigate to
 *
 * # Inserts the binded function call event
 * Topic: BackButton.functionCall
 * Value: {Object} Arguments object, args.func - function to execute,
 *                                   args.pushState - a new pushstate to be pushed
 *
 * # Inserts an event which triggers jQuery element click
 * Topic: BackButton.click
 * Value: {Object} Arguments object, args.element - jQuery selector or obj,
 *                                   args.pushState - a new pushstate to be pushed
 *
 * # Inserts a call to OffCanvas component method
 * Topic: BackButton.offCanvas
 * Value: {Object} Arguments object, args.method - jQuery selector or obj,
 *                                   args.scope - a scope to be binded to function call, null by default
 *                                   args.args - arguments to be passed to function call
 *                                   args.pushState - a new pushstate to be pushed
 *
 * # Removes last event from history stack
 * Topic: BackButton.removeLast
 * Value: {void} value is not required
 *
 * # Clears all history events
 * Topic: BackButton.reset
 * Value: {void} value is not required
 */

import jQuery from 'jquery';
import _ from 'lodash';
import PageCommon from '../pages/PageCommon';
import OffCanvas from './OffCanvas';
import DeviceDetector from './DeviceDetector';
import MessageBusTopics from '../../common/MessageBusTopics';

/**
 * Global PubSub reference
 *
 * @type {Object}
 *
 * @private
 */
var pubSub = window.WH.messageBus;


/**
 * A reference to previous window.onpopstate function, if there is one
 *
 * @type {Object}
 *
 * @private
 */
var oldOnpopstate = null;

/**
 * History events array
 *
 * @type {Array}
 *
 * @private
 */
/*jshint -W079 */
let history = [];

/**
 * jQuery back button object
 *
 * @type {String}
 *
 * @private
 */
var buttonSelector = '#back-button';

/**
 * A flag used to mark the next incoming onpopstate to be ignored
 *
 * @type {Boolean}
 *
 * @private
 */
var ignoreNextOnPopState = false;

/**
 * Store a flag that indicates if the device is mobile
 *
 * @type {Boolean}
 *
 * @private
 */
var isMobileDevice = DeviceDetector.isMobile() && !DeviceDetector.isTablet();

/**
 * Object holds actions related to back button click
 *
 * @type {Object}
 *
 * @private
 */
var clickActions = {

    /**
     * Function uses PageCommon.navigate to navigate in page
     *
     * @param {String|Object} path to navigate to or jQuery element containing href
     *
     * @returns {Boolean} returns false if in page navigation was successful
     *
     * @private
     */
    navigate: function navigate(path) {

        return PageCommon.navigate(path, true, true);
    },

    /**
     * Function triggers click event on jQuery element
     *
     * @param {Object} args jQuery element or selector and new pushState path (Optional)
     *
     * @returns {void}
     *
     * @private
     */
    click: function click(args) {

        if (!_.isObject(args.element)) {

            args.element = jQuery(args.element);
        }

        args.element.click();
        checkHistoryGoBack(args);
    },

    /**
     * Function triggers history.go(-1) and ensures that last history event is updated
     *
     * @param {Number} step Integer step value
     *
     * @returns {void}
     *
     * @private
     */
    history: function history(step) {

        step = (step) ? step : -1;
        ignoreNextOnPopState = true;
        window.history.go(step);
    },

    /**
     * Function triggers a binded function call
     *
     * @param {Object} args An Object containing function to be called and new pushState path (Optional)
     *
     * @returns {void}
     *
     * @private
     */
    functionCall: function functionCall(args) {

        if (_.isFunction(args.func)) {

            args.func();
            checkHistoryGoBack(args);
        }
    },

    /**
     * Function calls OffCanvas method with args provided within args Object
     *
     * @param {Object} args Method, arguments, func scope object and new pushState path (Optional)
     *
     * @returns {void}
     *
     * @private
     */
    offCanvas: function offCanvas(args) {

        var scope = getOffCanvasScope(args);

        if (args.method && _.isFunction(OffCanvas[args.method]) && isMobileDevice) {

            OffCanvas[args.method].apply(scope, args.args);
            checkHistoryGoBack(args);
        }
    },
};

/**
 * Returns OffCanvas scope
 *
 * @param {Object} args Method, arguments, func scope object and new pushState path (Optional)
 *
 * @returns {Object} Off-Canvas Scope
 *
 * @private
 */
var getOffCanvasScope = function getOffCanvasScope(args) {

    return (args.scope) ? args.scope : null;
};

/**
 * Should actual location be ignored in history.
 * If it's ignored instead of pushing it to history array, last history entry value will be decremented
 *
 * @param {Object} lastHistoryEntry Last item in history array
 *
 * @returns {Boolean} ignoreInHistory
 */
var ignoreInHistory = function ignoreInHistory(lastHistoryEntry) {

    var ignoredPaths = [
        /^\/betting\/([^/]+)\/([^/]+)\/matches/,
        /^\/betting\/([^/]+)\/in-play\/([^/]+)/,
        /^\/betting\/([^/]+)\/(horse-racing|greyhounds)\/(OB_EV[0-9]+)\/.*/,
    ];

    return _.some(ignoredPaths, function (ignoredPath) {

        return ignoredPath.test(window.location.pathname) && ignoredPath.test(lastHistoryEntry.url);
    });
};

/**
 * Object holds actions related to back button history array manipulation
 *
 * @type {Object}
 *
 * @private
 */
var historyActions = {

    /**
     * Pushes new event to history, if the path is defined, it will push new state to the browser
     *
     * @param {Object} args An object related to the action
     * @param {String} clickAction A click action name
     *
     * @returns {void}
     */
    addHistoryEvent: function addHistoryEvent(args, clickAction) {

        if (args && !_.isUndefined(args.pushState)) {

            args.pushState = pushNewState(args.pushState);
        }

        var lastHistoryEntry = _.last(history);

        var handleEvent = function handleEvent() {

            // if last history entry is in the same section (e.g. matches view) decrease last item value
            if (lastHistoryEntry && ignoreInHistory(lastHistoryEntry)) {

                lastHistoryEntry.value -= 1;
            } else {

                history.push({
                    action: clickAction,
                    value: args,
                    url: window.location.pathname,
                });
            }
        };

        handleEvent();
    },

    /**
     * Function removes last event from history
     *
     * @returns {Object} last event from history array
     *
     * @private
     */
    removeLast: function removeLast() {

        var event = null;

        if (history.length > 0) {

            event = history.pop();
        }
        return event;
    },

    /**
     * Function resets all history
     *
     * @returns {void}
     *
     * @private
     */
    reset: function reset() {

        history = [];
    },
};

/**
 * Check if history contains elements different then from current section.
 * The only supported section is matches view.
 *
 * @param {Array} history History Array
 * @param {Object} lastHistoryEntry Last History Entry
 *
 * @returns {Boolean} hasHistoryOutsideSelection
 */
var hasHistoryOutsideSection = function hasHistoryOutsideSection(history, lastHistoryEntry) {

    if (history.length === 1) {

        return !ignoreInHistory(lastHistoryEntry);
    }

    return true;
};

/**
 * A binded function to back button click event, it executes clickAction if history is not empty,
 * otherwise it will navigate to buttons href value
 *
 * @returns {Boolean} returns false to stop click event propagation
 *
 * @private
 */
var buttonClick = function buttonClick() {

    var lastHistoryEntry = _.last(history);

    if (lastHistoryEntry && hasHistoryOutsideSection(history, lastHistoryEntry)) {

        var event = historyActions.removeLast();
        callClickAction(event);
    } else {

        // We can clear history, after every return to homepage we are creating new one
        // we can never go back to items that were there
        historyActions.reset();

        //Otherwise let navigate to where button links to - home page
        clickActions.navigate(jQuery(buttonSelector).data('href'), true, true);
    }

    return false;
};

/**
 * Calls a clickActions method if there is one
 *
 * @param {Object} event History event object
 *
 * @returns {void}
 *
 * @private
 */
var callClickAction = function callClickAction(event) {

    if (event && _.isFunction(clickActions[event.action])) {

        clickActions[event.action](event.value);
    }
};

/**
 * Subscribes to PubSub and listens for published messages, to take actions against history events array
 *
 * @param {String} topic PubSub topic
 * @param {Integer|String|Object} value PubSub value
 *
 * @returns {void}
 *
 * @private
 */
var pubSubMessagesListener = function pubSubMessagesListener(topic, value) {

    var topicArgs = topic.split('.');
    var action = topicArgs[1],
        newEventSubTopics = ['history', 'navigate', 'functionCall', 'click', 'offCanvas'],
        clickAction = null;

    if (_.indexOf(newEventSubTopics, topicArgs[1]) !== -1) {

        action = 'addHistoryEvent';
        clickAction = topicArgs[1];
    }

    callHistoryAction(action, value, clickAction);
};

/**
 * Function checks if action is allowed. For example 'offCanvas' is only allowed for mobile.
 *
 * @param {String} clickAction a string value of clickAction
 *
 * @returns {Boolean} is the action allowed
 *
 * @private
 */
var isActionAllowed = function isActionAllowed(clickAction) {

    return !(clickAction === 'offCanvas' && !isMobileDevice);
};

/**
 * Function checks if action actually exists within historyActions, before calling it.
 *
 * @param {String} action Is historyActions function name
 * @param {Integer|String|Object} value PubSub value
 * @param {String} clickAction a string value of clickAction
 *
 * @returns {void}
 *
 * @private
 */
var callHistoryAction = function callHistoryAction(action, value, clickAction) {

    if (_.isFunction(historyActions[action]) && isActionAllowed(clickAction)) {

        historyActions[action](value, clickAction);
    }
};

/**
 * Triggers new pushState to page without triggering the page re-render
 *
 * @param {String} path A path to push a new state to router
 *
 * @returns {String} currentPath Current Path returned if no path supplied
 *
 * @private
 */
var pushNewState = function pushNewState(path) {

    //Save current state. Dont translate this url it should be as it is
    var currentPath = window.location.pathname;
    if (path) {

        PageCommon.navigate(path, false, true);
    }

    return currentPath;
};

/**
 * This function checks if pushState was set within the args and if so it executes history go back, because we
 * have triggered a pushstate before new event was inserted
 *
 * @param {Object} args An action args
 *
 * @returns {void}
 *
 * @private
 */
var checkHistoryGoBack = function checkHistoryGoBack(args) {

    if (args && !_.isUndefined(args.pushState)) {

        clickActions.history(-1);
    }
};

/**
 * Makes sure to keep a reference to prev window.onpopstate to ensure it's gonna be executed before newly
 * binded functionality
 *
 * @returns {void}
 *
 * @private
 */
var handleOnPopState = function handleOnPopState() {

    oldOnpopstate = window.onpopstate;
    window.onpopstate = onPopState;
};

/**
 * Tracks window popstate events changes, if user clicked browser's back or forward button, it resets
 * history, otherwise it might run into unexpected scenarios
 *
 * @returns {void}
 *
 * @private
 */
var onPopState = function onPopState() {

    handleOldPopState.apply(this, arguments);

    if (ignoreNextOnPopState === true) {

        ignoreNextOnPopState = false;
    } else {

        // It's safer to reset history, as we don't know the direction
        historyActions.reset();
    }
};

/**
 * Applys a call to pre-saved old window.onpopstate function call, if it was function
 *
 * @returns {void}
 *
 * @private
 */
var handleOldPopState = function handleOldPopState() {

    if (_.isFunction(oldOnpopstate)) {

        oldOnpopstate.apply(this, arguments);
    }
};

/**
 * Public functions.
 *
 * @type {Object}
 */
var api = {

    /**
     * Initialization of back button
     *
     * @returns {void}
     *
     * @public
     */
    init: function init() {

        // Set initial history item with starting url
        historyActions.addHistoryEvent(-1, 'history');

        handleOnPopState();
        pubSub.subscribe(MessageBusTopics.BACK_BUTTON, pubSubMessagesListener);
        jQuery('#main-off-canvas').on('click', buttonSelector, buttonClick);
    },
};

export default api;
